
import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: string) => void;
}

const icons = {
  success: <CheckCircle className="w-5 h-5 text-green-500" />,
  error: <AlertCircle className="w-5 h-5 text-red-500" />,
  warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
  info: <Info className="w-5 h-5 text-blue-500" />
};

const styles = {
  success: "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200",
  error: "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
  warning: "border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200",
  info: "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200"
};

const ToastItem: React.FC<{ toast: Toast; removeToast: (id: string) => void }> = ({ toast, removeToast }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      removeToast(toast.id);
    }, 4000); // 4 saniye sonra otomatik kapan
    return () => clearTimeout(timer);
  }, [toast.id, removeToast]);

  return (
    <div className={`flex items-center w-full max-w-sm p-4 mb-3 rounded-xl shadow-lg border-l-4 animate-in slide-in-from-right-full transition-all duration-300 ${styles[toast.type]} bg-white dark:bg-gray-800`}>
      <div className="flex-shrink-0">
        {icons[toast.type]}
      </div>
      <div className="ml-3 text-sm font-medium pr-4">
        {toast.message}
      </div>
      <button 
        onClick={() => removeToast(toast.id)} 
        className="ml-auto -mx-1.5 -my-1.5 rounded-lg p-1.5 inline-flex h-8 w-8 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
      >
        <X className="w-4 h-4 opacity-70" />
      </button>
    </div>
  );
};

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col items-end space-y-2 pointer-events-none">
      <div className="pointer-events-auto">
         {toasts.map(toast => (
           <ToastItem key={toast.id} toast={toast} removeToast={removeToast} />
         ))}
      </div>
    </div>
  );
};
